import { useState } from 'react';
import { ProtocolReview } from '../../../shared/types/protocol.ts';
import { useProtocolStore } from '../../pages/protocol-view/protocolStoreProvider.tsx';
import { PositionFilterChoice } from '../../pages/protocol-view/usePositionFilter.ts';
import { ProtocolDraft } from '../../types/sharedTypeImpl.ts';
import { openLinkInNewTab } from '../../utils/navigation.ts';
import { ProtocolFormPositions } from './protocolFormPositions.tsx';
import { ProtocolFormReview } from './protocolFormReview.tsx';
import { ProtocolMetaFields } from './protocolMetaFields.tsx';
import { SubmitButtonBar } from './submitButtonBar.tsx';
import { useFormKeyboardNavigation } from './useFormKeyboardNavigation.ts';
import { validateProtocolDraft, validateProtocolReview } from './validateProtocolForm.ts';


interface Props {
    className?: string;
    mode: 'edit' | 'view' | 'review';
    pdfData: Uint8Array | null;
    onSetPdfFile: (file: File) => void;
    showAllMarkers: boolean;
    setShowAllMarkers: (showAllMarkers: boolean) => void;
    showAllPosNrs: boolean;
    setShowAllPosNrs: (showAllPosNrs: boolean) => void;
    positionFilter: PositionFilterChoice;
    filterOptions: PositionFilterChoice[];
    visiblePositionIds: ReadonlySet<number>;
    onPositionFilterChange: (value: PositionFilterChoice) => void;
    isDirty: boolean;
    isSaving: boolean;
    isFinalSaving: boolean;
    onDraftSave: () => void;
    onFinalSave: (protocol: ProtocolDraft) => void;
    onReviewSave: (review: ProtocolReview) => void;
    onCloseClicked: () => void;
}

export function ProtocolForm({
    className,
    mode,
    pdfData,
    onSetPdfFile,
    showAllMarkers,
    setShowAllMarkers,
    showAllPosNrs,
    setShowAllPosNrs,
    positionFilter,
    filterOptions,
    visiblePositionIds,
    onPositionFilterChange,
    isDirty,
    isSaving,
    isFinalSaving,
    onDraftSave,
    onFinalSave,
    onReviewSave,
    onCloseClicked,
}: Props) {

    const isFormReadOnly = mode === 'view' || mode === 'review';
    const [reviewer, setReviewer] = useState('');
    const [reviewComment, setReviewComment] = useState('');

    const protocol = useProtocolStore(state => state.protocol);
    const setProtocolField = useProtocolStore(state => state.setProtocolField);
    const deleteProtocolField = useProtocolStore(state => state.deleteProtocolField);
    const submitMeasurementPlan = useProtocolStore(state => state.submitMeasurementPlan);

    const formRef = useFormKeyboardNavigation();


    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!protocol) return;
        if (mode !== 'edit') return;

        if (!pdfData) {
            alert('Must set a pdf');
            return;
        }
        if (!validateProtocolDraft(protocol)) {
            return;
        }

        onFinalSave(protocol);
    }

    const handleSaveDraft = (e: React.MouseEvent) => {
        e.preventDefault();
        onDraftSave();
    }

    const handleApproveClicked = (e: React.MouseEvent) => {
        e.preventDefault();
        if (!protocol) return;

        const review = {
            approved: true,
            operator: reviewer,
        };

        if (!validateProtocolReview(review, protocol)) {
            return;
        }
        if (confirm('Approve?')) {
            onReviewSave({ approved: true, operator: reviewer });
        }
    }

    const handleRejectClicked = (e: React.MouseEvent) => {
        e.preventDefault();
        if (!protocol) return;

        const review = {
            approved: false,
            operator: reviewer,
            comment: reviewComment,
        };

        if (!validateProtocolReview(review, protocol)) {
            return;
        }
        if (confirm('Reject?')) {
            onReviewSave({ approved: false, operator: reviewer, comment: reviewComment });
        }
    }

    const handleCloseClicked = (e: React.MouseEvent) => {
        e.preventDefault();
        onCloseClicked();
    }


    if (!protocol) return null;
    return (
        <div className={`${className} measurement-form`}>
            <button
                onClick={() => openLinkInNewTab('https://berryglade.sharepoint.com/:w:/s/BGNET/EQqUpUnfYEtHkUFOiuILpRMBhOc9JOqz4pizmpFhLq1B5w?e=ECJOby')}
                children='Help'
            />

            <form
                ref={formRef}
                onSubmit={handleSubmit}
            >
                <table className='record-form--table'>
                    <tbody>
                        <ProtocolMetaFields
                            protocol={protocol}
                            readOnly={isFormReadOnly}
                            setProtocolField={setProtocolField}
                            deleteProtocolField={deleteProtocolField}
                            onSetPdfFile={onSetPdfFile}
                            onSubmitMeasurementPlan={submitMeasurementPlan}
                        />
                    </tbody>
                </table>

                <ProtocolFormPositions
                    isReadOnly={isFormReadOnly}
                    showAllMarkers={showAllMarkers}
                    setShowAllMarkers={setShowAllMarkers}
                    showAllPosNrs={showAllPosNrs}
                    setShowAllPosNrs={setShowAllPosNrs}
                    positionFilter={positionFilter}
                    filterOptions={filterOptions}
                    visiblePositionIds={visiblePositionIds}
                    onPositionFilterChange={onPositionFilterChange}
                />

                <ProtocolFormReview
                    protocol={protocol}
                    mode={mode}
                    reviewer={reviewer}
                    setReviewer={setReviewer}
                    reviewComment={reviewComment}
                    setReviewComment={setReviewComment}
                />

                <SubmitButtonBar
                    mode={mode}
                    isDirty={isDirty}
                    isSaving={isSaving}
                    isFinalSaving={isFinalSaving}
                    onDraftSaveClick={handleSaveDraft}
                    onApproveClick={handleApproveClicked}
                    onRejectClick={handleRejectClicked}
                    onCloseClick={handleCloseClicked}
                />
            </form>
        </div>
    );
}
